import { useCallback, useMemo, useState } from 'react';
import { AssetRow } from 'components/AssetsBrowser/types';
import { NewFolderRow } from './atoms';
import {
  Table,
  tableOperations,
  type Column,
  type Row as TableRow,
} from '@ardoq/table';
import { RowType } from '@ardoq/table-legacy';
import { withErrorBoundaries } from '@ardoq/error-boundary';
import { logError } from '@ardoq/logging';
import { Folder, Row } from 'components/EntityBrowser/types';
import { TextEllipsisWrapper } from 'components/EntityBrowser/atoms';
import { FlexBox } from '@ardoq/layout';
import { Icon, IconName } from '@ardoq/icons';
import { SearchInput } from '@ardoq/forms';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { type I18nLocale, t } from '@ardoq/i18n';
import { type Locale, localeCompare } from '@ardoq/locale';
import { SelectedFolder } from './types';
import {
  clearSearchMask,
  countVisibleRows,
  resolveExpandedIds,
  searchFolderRows,
  toggleExpansion,
  type ExpansionState,
  type FolderGroupData,
  type FolderRowData,
} from './folderSearch';

const NO_FOLDER_ROW_ID = '__no_folder__';

type FolderSelectProps = {
  /** Off where the surrounding dialog is itself a create-folder form. */
  canCreateFolder?: boolean;
  selectedFolder: SelectedFolder;
  setSelectedFolder: (folder: SelectedFolder) => void;
  dataSource: Row<AssetRow>[];
  i18nLocale: I18nLocale;
  locale: Locale;
};

const FolderNameCell = ({ name, isNoFolder }: FolderGroupData) => (
  <FlexBox gap="small" align="center">
    {!isNoFolder && <Icon iconName={IconName.FOLDER} />}
    <TextEllipsisWrapper>{name}</TextEllipsisWrapper>
  </FlexBox>
);

// Pins the "No folder" row to the top, then sorts folders alphabetically.
const compareFolderRows =
  (locale: Locale) =>
  (a: FolderGroupData | null, b: FolderGroupData | null): number => {
    if (a?.isNoFolder) return -1;
    if (b?.isNoFolder) return 1;
    return localeCompare(a?.name ?? '', b?.name ?? '', locale);
  };

const isFolderRow = (node: Row<AssetRow>): node is Folder<AssetRow> =>
  node.rowType === RowType.FOLDER;

const flattenFolderTree = (
  tree: Row<AssetRow>[],
  parentPath: string[] = []
): TableRow<FolderRowData>[] =>
  tree.flatMap(node => {
    if (!isFolderRow(node)) return [];
    const path = [...parentPath, node._id];
    return [
      { id: node._id, path, data: { _group: { name: node.name } } },
      ...flattenFolderTree(node.children ?? [], path),
    ];
  });

const FolderSelectBase = ({
  canCreateFolder = true,
  selectedFolder,
  setSelectedFolder,
  dataSource,
  i18nLocale,
  locale,
}: FolderSelectProps) => {
  const [expansion, setExpansion] = useState<ExpansionState>({
    manualIds: [],
    collapsedIds: [],
  });
  const [searchPhrase, setSearchPhrase] = useState('');
  const trimmedSearchPhrase = searchPhrase.trim();

  const selectedRowId = selectedFolder?._id ?? NO_FOLDER_ROW_ID;

  const rows = useMemo((): TableRow<FolderRowData>[] => {
    const noFolderRow: TableRow<FolderRowData> = {
      id: NO_FOLDER_ROW_ID,
      path: [NO_FOLDER_ROW_ID],
      data: {
        _group: {
          name: t(i18nLocale, 'folderSelectDialog:noFolder', 'No folder'),
          isNoFolder: true,
        },
      },
    };
    return [noFolderRow, ...flattenFolderTree(dataSource)];
  }, [dataSource, i18nLocale]);

  const {
    matchCount,
    rowsToRender,
    expandedIds: searchExpandedIds,
  } = useMemo(
    () =>
      searchFolderRows({
        rows,
        searchPhrase: trimmedSearchPhrase,
        selectedRowId,
        locale,
      }),
    [rows, trimmedSearchPhrase, selectedRowId, locale]
  );

  const expandedIds = useMemo(
    () =>
      resolveExpandedIds({
        manualIds: expansion.manualIds,
        searchIds: searchExpandedIds,
        collapsedIds: expansion.collapsedIds,
      }),
    [expansion, searchExpandedIds]
  );

  const onSearchPhraseChange = (phrase: string) => {
    setSearchPhrase(phrase);
    if (phrase.trim() === trimmedSearchPhrase) return;

    setExpansion(clearSearchMask);
  };

  const onRowExpansionToggle = useCallback(
    (rowId: string, isExpanded: boolean) =>
      setExpansion(current =>
        toggleExpansion({ expansion: current, expandedIds, rowId, isExpanded })
      ),
    [expandedIds]
  );

  const onExpandedIdsChange = useCallback(
    (ids: string[]) =>
      setExpansion({
        manualIds: ids,
        collapsedIds: searchExpandedIds.filter(id => !ids.includes(id)),
      }),
    [searchExpandedIds]
  );

  const resetSearch = () => setSearchPhrase('');

  // A folder only becomes an expandable node once it has a child, so revealing
  // the new folder means opening its parent. Clearing the phrase would fold
  // away the branch it was created in, so what is open becomes the user's own.
  const onFolderCreated = (parentFolderId: string | null) => {
    resetSearch();
    setExpansion({
      manualIds: [
        ...new Set([
          ...expandedIds,
          ...(parentFolderId ? [parentFolderId] : []),
        ]),
      ],
      collapsedIds: [],
    });
  };

  const folderNamesById = useMemo(
    () => new Map(rows.map(row => [row.id, row.data._group.name])),
    [rows]
  );

  const columns: Column<FolderRowData>[] = useMemo(
    () => [
      {
        id: '_group',
        title: 'Folder',
        expandedIds,
        onExpandedIdsChange,
        onRowExpansionToggle,
        valueGetter: ({ name }) => name,
        comparator: compareFolderRows(locale),
        InnerCellRenderer: FolderNameCell,
      },
    ],
    [expandedIds, onExpandedIdsChange, onRowExpansionToggle, locale]
  );

  const visibleRowCount = useMemo(
    () => countVisibleRows(rowsToRender, expandedIds),
    [rowsToRender, expandedIds]
  );

  const emptyStateLabels = useMemo(
    () => ({
      noResultsTitle: t(
        i18nLocale,
        'folderSelectDialog:noSearchResultsTitle',
        'No folders found'
      ),
      noResultsDescription: t(
        i18nLocale,
        'folderSelectDialog:noSearchResultsDescription',
        'Try a different search term or check the spelling.'
      ),
    }),
    [i18nLocale]
  );

  const searchLabel = t(
    i18nLocale,
    'folderSelectDialog:searchPlaceholder',
    'Search folders'
  );

  const matchCountMessage =
    matchCount === 1
      ? t(
          i18nLocale,
          'folderSelectDialog:searchResultSingular',
          '1 folder found'
        )
      : t(
          i18nLocale,
          'folderSelectDialog:searchResultPlural',
          { count: matchCount },
          '{{count}} folders found'
        );

  return (
    <FlexBox flexDirection="column" gap="small" height="full">
      <SearchInput
        value={searchPhrase}
        placeholder={searchLabel}
        aria-label={searchLabel}
        dataTestId="folder-search-input"
        data-autofocus
        onValueChange={onSearchPhraseChange}
        i18nLocale={i18nLocale}
      />
      <VisuallyHidden role="status" aria-live="polite">
        {trimmedSearchPhrase === '' ? '' : matchCountMessage}
      </VisuallyHidden>
      <Table
        headerHidden
        height={
          visibleRowCount > 7
            ? '100%'
            : `${tableOperations.getDynamicTableHeight(
                visibleRowCount,
                visibleRowCount,
                { excludeHeaderHeight: true }
              )}px`
        }
        rows={rowsToRender}
        columns={columns}
        emptyStateLabels={emptyStateLabels}
        sortable={{ descriptor: { column: '_group', direction: 'asc' } }}
        selectable={{
          mode: 'single',
          id: selectedFolder?._id ?? NO_FOLDER_ROW_ID,
          onChange: id =>
            setSelectedFolder(
              id === null || id === NO_FOLDER_ROW_ID
                ? null
                : { name: folderNamesById.get(id) ?? '', _id: id }
            ),
        }}
        aria-label={t(
          i18nLocale,
          'folderSelectDialog:ariaLabel',
          'Folder browser'
        )}
      />
      {canCreateFolder && (
        <NewFolderRow
          selectedFolder={selectedFolder}
          setSelectedFolder={setSelectedFolder}
          onFolderCreated={onFolderCreated}
          i18nLocale={i18nLocale}
        />
      )}
    </FlexBox>
  );
};

export const FolderSelect = withErrorBoundaries({
  errorContextDescription: 'Error at <FolderSelect />',
  logError,
})(FolderSelectBase);
